import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";

// API Hooks (assuming they are in these paths)
import UseSentMutation from "../api/mutation/useSentMutation";
import { useAllChatsQuery } from "../api/query/useAllChatsQuery";
import { useChatByIdQuery } from "../api/query/useChatByIdQuery";
import { useCreateOrFetchUserMutation } from "../api/mutation/useCreateOrFetchUserMutation";
import { useCreateThredMutaion } from "../api/mutation/useCreateThredMutaion";

// Components
import { Sidebar } from "../components/common/Sidebar";
import { ChatBox } from "../components/common/ChatBox";
import { Input } from "../components/common/Input";
import DefaultPrompt from "../components/common/defaultPrompt";
import ToggleSwitch from "../components/darkToggle";

// Assets
import SidebarOpen from "../assest/svg/sidebar_open.svg";
import Loader from "../assest/loader.svg";
import NewTab from "../assest/svg/newTab.svg";
import { clsx } from "clsx";
// --- TYPE DEFINITIONS ---

export type Message = {
  role: string;
  conversation_id: string;
  timestamp: string;
  content: string;
  image_urls: any[];
  id: string;
};

interface ChatUIProps {
  isBubble?: boolean;
  parentUrl: string;
}

interface UseChatManagerProps {
  threadId: string | null;
  userId: string | null;
  parentUrl: string;
  onSendSuccess: () => void;
}

// --- CUSTOM HOOKS ---

/**
 * Handles fetching or creating a user ID and storing it in localStorage.
 */
const useAuthentication = () => {
  const [userId, setUserId] = useState<string | null>(
    localStorage.getItem("user_id")
  );
  const fetchUserMutation = useCreateOrFetchUserMutation();

  useEffect(() => {
    if (!userId) {
      fetchUserMutation.mutate(undefined, {
        onSuccess: (data) => {
          const newUserId = data.data.data.user_id;
          setUserId(newUserId);
          localStorage.setItem("user_id", newUserId);
        },
        onError: (error) => {
          console.error("Failed to fetch or create user:", error);
        },
      });
    }
  }, []);

  return {
    userId,
    isLoading: fetchUserMutation.isPending && !userId,
  };
};

/**
 * Manages the current chat thread ID, interacts with URL search parameters,
 * and handles new chat creation.
 */
const useThreadManager = (onNewChat: () => void) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [threadId, setThreadId] = useState<string | null>(() =>
    searchParams.get("thread_id")
  );
  const [isInitializing, setIsInitializing] = useState(true);
  const createThreadMutation = useCreateThredMutaion();

  useEffect(() => {
    setThreadId(searchParams.get("thread_id") ?? null);
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.get("thread_id")) {
      setIsInitializing(false);
      return;
    }

    const threadIdFromStorage = localStorage.getItem("thread_id");
    if (threadIdFromStorage) {
      setThreadId(threadIdFromStorage);
      setSearchParams({ thread_id: threadIdFromStorage }, { replace: true });
      setIsInitializing(false);
      return;
    }

    if (!createThreadMutation.isPending) {
      createThreadMutation.mutate(undefined, {
        onSuccess(data) {
          const newThreadId = data.data.thread_id;
          setThreadId(newThreadId);
          setSearchParams({ thread_id: newThreadId }, { replace: true });
          setIsInitializing(false);
        },
        onError(error) {
          console.error("Failed to create initial thread:", error);
          setIsInitializing(false);
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectThread = useCallback(
    (newThreadId: string) => {
      setThreadId(newThreadId);
      setSearchParams({ thread_id: newThreadId });
    },
    [setSearchParams]
  );

  const createNewChat = useCallback(() => {
    setIsInitializing(true);
    onNewChat();
    createThreadMutation.mutate(undefined, {
      onSuccess(data) {
        const newThreadId = data.data.thread_id;
        selectThread(newThreadId);
        setIsInitializing(false);
      },
      onError(error) {
        console.error("Failed to create new thread:", error);
        setIsInitializing(false);
      },
    });
  }, [onNewChat, createThreadMutation, selectThread]);

  return { threadId, isInitializing, selectThread, createNewChat };
};

/**
 * Manages the state and logic for a single chat conversation, including
 * messages, input, sending, and streaming.
 */
const useChatManager = ({
  threadId,
  userId,
  parentUrl,
  onSendSuccess,
}: UseChatManagerProps) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [image, setImage] = useState<File[]>([]);

  const [isStreaming, setIsStreaming] = useState(false);
  const [tempStreamMsgId, setTempStreamMsgId] = useState("");
  const [cancelStream, setCancelStream] = useState(false);

  const { data: chatHistory, isFetching: isHistoryFetching } = useChatByIdQuery(
    threadId ?? "",
    Boolean(threadId)
  );

  useEffect(() => {
    setMessages(chatHistory?.data?.data ?? []);
  }, [chatHistory]);

  const handleStreamMessage = (messageChunk: string) => {
    if (!isStreaming) setIsStreaming(true);

    setMessages((prev) => {
      const existingMessage = prev.find((msg) => msg.id === tempStreamMsgId);
      if (existingMessage) {
        return prev.map((msg) =>
          msg.id === tempStreamMsgId
            ? { ...msg, content: msg.content + messageChunk }
            : msg
        );
      }
      return [
        ...prev,
        {
          content: messageChunk,
          role: "system",
          id: tempStreamMsgId,
          conversation_id: "",
          timestamp: "",
          image_urls: [],
        },
      ];
    });
  };

  const handleStreamError = (error: any) => {
    console.error("Streaming Error:", error);
    toast.error(error.detail || "An error occurred.");
    setIsStreaming(false);
  };

  const handleStreamComplete = () => {
    setIsStreaming(false);
    onSendSuccess();
    if (localStorage.getItem("thread_id") === threadId) {
      localStorage.removeItem("thread_id");
    }
  };

  // FIX 1: Removed the 5th argument `handleStreamComplete`
  const mutation = UseSentMutation(
    handleStreamMessage,
    handleStreamError,
    cancelStream,
    setCancelStream
  );

  const handleSend = (prompt?: string) => {
    const messageContent = prompt || input;
    if (!messageContent.trim() && image.length === 0) return;
    if (!userId || !threadId) {
      toast.warn("User or Chat session not ready.");
      return;
    }

    const newTempId = uuidv4();
    setTempStreamMsgId(newTempId);

    setMessages((prev) => [
      ...prev,
      {
        content: messageContent,
        role: "user",
        id: uuidv4(),
        image_urls: image.map(URL.createObjectURL),
        conversation_id: "",
        timestamp: "",
      },
    ]);

    const formData = new FormData();
    formData.append("prompt", messageContent);
    formData.append("user_id", userId);
    formData.append("thread_id", threadId);
    formData.append("source_url", parentUrl || window.location.href);
    image.forEach((img) => formData.append("images", img));

    // FIX 2: Call `handleStreamComplete` inside the onSuccess callback
    mutation.mutate(formData, {
      onSuccess: () => {
        handleStreamComplete();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || "Failed to send message.");
        setIsStreaming(false);
      },
    });

    setInput("");
    setImage([]);
  };

  const cancelApiRequest = () => {
    setCancelStream(true);
    setIsStreaming(false);
  };

  return {
    messages,
    setMessages,
    input,
    setInput,
    image,
    setImage,
    handleSend,
    isLoading: mutation.isPending || isHistoryFetching,
    isStreaming,
    cancelApiRequest,
  };
};

// --- MAIN COMPONENT ---

const ChatUI = ({ isBubble = false, parentUrl }: ChatUIProps) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(!isBubble);
  const chatBoxRef = useRef<HTMLDivElement | null>(null);

  const { userId, isLoading: isAuthLoading } = useAuthentication();
  const { data: allChatsData, refetch: refetchAllChats } =
    useAllChatsQuery(userId);

  const [searchParams] = useSearchParams();
  const currentThreadId = searchParams.get("thread_id");

  const {
    messages,
    setMessages,
    input,
    setInput,
    image,
    setImage,
    handleSend,
    isLoading: isChatLoading,
    isStreaming,
    cancelApiRequest,
  } = useChatManager({
    threadId: currentThreadId,
    userId,
    parentUrl,
    onSendSuccess: () => refetchAllChats(),
  });

  const {
    threadId,
    isInitializing: isThreadInitializing,
    selectThread,
    createNewChat,
  } = useThreadManager(() => setMessages([]));

  const isInitializing = isAuthLoading || isThreadInitializing;
  const sortedChats =
    allChatsData?.data.data.sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    ) ?? [];

  return (
    <div className="flex h-full w-full relative bg-[#F8FAFC] dark:bg-[#090A0F] text-slate-900 dark:text-[#e3e1e9] font-sans transition-colors overflow-hidden">
      {/* Loading overlay */}
      {isInitializing && (
        <div className="absolute top-0 left-0 h-full w-full z-50 bg-black/50 backdrop-blur-sm flex justify-center items-center">
          <img
            className="w-16 h-16 animate-spin"
            alt="Chargement..."
            src={Loader}
          />
        </div>
      )}

      {/* --- 1. SIDEBAR --- */}
      <div
        className={clsx(
          "h-full transition-all duration-300 z-30 flex-shrink-0",
          {
            // Bubble Mode Logic
            "absolute w-full h-full top-0 left-0 z-40 rounded-xl overflow-hidden":
              isBubble && isSidebarOpen,
            // Standard Mode Logic
            "w-auto": !isBubble && isSidebarOpen,
            "w-0 min-w-0 overflow-hidden": !isSidebarOpen,
          }
        )}
      >
        <Sidebar
          messages={sortedChats}
          thread_id={threadId ?? ""}
          onClose={() => setIsSidebarOpen(false)}
          selectedChatId={selectThread}
          newChat={createNewChat}
          isBubble={isBubble}
        />
      </div>

      {/* --- 2. MAIN CONTENT & CHAT AREA --- */}
      <main
        className={`flex-1 min-w-0 flex flex-col h-full relative ${
          isBubble ? "rounded-xl" : ""
        }`}
      >
        {/* Header */}
        <header
          className={`flex-shrink-0 border-b border-slate-200/80 dark:border-white/5 bg-white/70 dark:bg-[#090A0F]/70 backdrop-blur-md z-20 ${
            isBubble ? "p-2.5" : "px-5 py-3"
          }`}
        >
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="p-1.5 flex items-center justify-center hover:bg-slate-200/60 dark:hover:bg-white/5 rounded-xl transition-colors text-slate-500 dark:text-slate-400"
                title={isSidebarOpen ? "Réduire la barre latérale" : "Développer la barre latérale"}
              >
                <img
                  className="w-4 h-4 dark:invert opacity-75 hover:opacity-100 transition-opacity"
                  src={SidebarOpen}
                  alt="Barre latérale"
                />
              </button>

              <div className="flex items-center gap-2 font-display text-xs font-semibold tracking-wider">
                <span className="text-slate-800 dark:text-slate-200 tracking-wider">CONTINUUM AI</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
                title="Ouvrir dans un nouvel onglet"
              >
                <span className="hidden sm:inline">Ouvrir dans un nouvel onglet</span>
                <img
                  src={NewTab}
                  alt="Ouvrir dans un nouvel onglet"
                  className="w-3.5 h-3.5 dark:invert opacity-60 hover:opacity-100 transition-opacity"
                />
              </a>

              <div className="flex items-center">
                <ToggleSwitch />
              </div>
            </div>
          </div>
        </header>

        {/* Scrollable Chat Container */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="max-w-4xl mx-auto w-full px-4 py-6">
            {messages.length > 0 ? (
              <ChatBox
                chatBox={chatBoxRef}
                key={threadId}
                messages={messages}
              />
            ) : !isBubble ? (
              <div className="flex flex-col items-center justify-center min-h-[62vh] py-6 animate-fadeIn">
                {/* Clean Status Pill */}
                <div className="inline-flex items-center gap-2 px-3.5 py-1 mb-5 rounded-full bg-slate-200/70 dark:bg-[#131622] border border-slate-300/60 dark:border-white/10 shadow-sm text-xs font-mono text-slate-700 dark:text-slate-300">
                  <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                  <span className="font-medium text-slate-900 dark:text-white">Continuum 1.0</span>
                </div>

                {/* Hero Title */}
                <div className="text-center mb-8">
                  <h1 className="text-4xl sm:text-5xl font-display font-bold text-slate-900 dark:text-white tracking-tight mb-2">
                    Continuum
                  </h1>
                  <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400 max-w-md mx-auto font-normal">
                    Donnez un élan de réussite à vos projets.
                  </p>
                </div>

                {/* Default Prompts Grid */}
                <div className="w-full max-w-3xl">
                  <DefaultPrompt onClick={handleSend} />
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {/* Chat Input Container */}
        <div className="flex-shrink-0 px-4 pt-1 pb-2 max-w-4xl mx-auto w-full">
          <Input
            loading={isChatLoading}
            onSubmit={handleSend}
            setInput={setInput}
            setImage={setImage}
            input={input}
            image={image}
            cancelApi={cancelApiRequest}
            fetching={isStreaming}
          />

          {!isBubble && (
            <div className="mt-2 flex items-center justify-center gap-1.5 text-[11px] text-slate-400 dark:text-slate-500">
              <span className="text-purple-500 text-xs">✦</span>
              <span>Continuum 1.0 : Powered by</span>
              <a
                href="https://blog-gestion-de-projet.com/"
                target="_blank"
                className="text-purple-600 dark:text-purple-400 hover:underline transition-colors font-medium ml-0.5"
                rel="noreferrer"
              >
                Blog Gestion de Projet
              </a>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ChatUI;
